import React, { createContext, useContext, useState, useEffect } from "react";
import { handleBackgroundScroll } from "../utils/backgroundScroll";
import { useSearchWindow } from "./SearchContext";
/**
* Represents the currently opened detail card.
*/
interface OpenedCard { 
    id: number, 
    type: 'sales' | 'storage' | 'package' | 'venue' | 'new' | 'activities' | undefined,
    time?: string,
    date?:string
}
/**
* Shape of the DetailContext value.
*/
interface DetailContextType {
    /** The currently opened detail card or undefined if none is open */
    openedDetailCard: OpenedCard | undefined;
    /**
    * Opens the detail view for a given card.
    * @param card - The card to open detail for
    */
    openDetail: (card: OpenedCard, fromSearch?: boolean, openedOnTop?: boolean) => void;
    /** Closes the currently opened detail view */
    closeDetail: () => void;
    /** Flag whether the detail was open from search popup */
    openedFromSearch: boolean
}

/**
* React context to manage detail card state.
* Provides the currently opened detail card and methods to open/close it.
*/
const DetailContext = createContext<DetailContextType | undefined>(undefined);
/**
* Provider component that wraps children and provides detail card state.
* Stops background scroll when a detail card is opened.
*/
export const DetailContextProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { open: openSearch } = useSearchWindow();
    const [openedFromSearch, setOpenedFromSearch] = useState(false);
    const [openedOnTop, setOpenedOnTop] = useState(false);
    const [openedDetailCard, setOpenedDetail] = useState<OpenedCard | undefined>(undefined) // Detail open flag
    const openDetail = (card: OpenedCard, fromSearch = false, openedOnTop = false) => {
        setOpenedFromSearch(fromSearch)
        setOpenedOnTop(openedOnTop)
        setOpenedDetail(card);   
    } // Open detail
    const closeDetail = () => {
        setOpenedDetail(undefined);
        if (openedFromSearch) {
            openSearch();
        }
        setOpenedFromSearch(false);
     } // Close detail
    
    // Stop scroll on open
    useEffect(()=>{
        if(!openedOnTop){
            handleBackgroundScroll(openedDetailCard !== undefined)
            setOpenedOnTop(false)
        }
    }, [openedDetailCard])

    return (
        <DetailContext.Provider value={{ openDetail, closeDetail, openedDetailCard, openedFromSearch }}>
        {children}
        </DetailContext.Provider>
    );
};
/**
* Hook to access the DetailContext value.
* Throws error if used outside of a DetailContextProvider.
*
* @returns The detail card context with open/close methods and current card info.
*/
export const useDetailContext = () => {
    const context = useContext(DetailContext);
    if (!context) throw new Error("useDetailContext must be used within a DetailContextProvider");
    return context;
};